/**
 * Dove tornare dopo essere entrati.
 *
 * Il middleware, quando rimbalza qualcuno sulla pagina di accesso, si porta
 * dietro l'indirizzo che stava cercando di aprire. Rimandarcelo dopo
 * l'accesso è comodo — un collegamento a «Utenti» aperto da un telefono non
 * ancora collegato porta a «Utenti», non alla pagina iniziale.
 *
 * Comodo e pericoloso insieme: se ci si fidasse di quel valore, chiunque
 * potrebbe far arrivare a qualcun altro un indirizzo `/accesso?torna=https://…`
 * che dopo l'accesso porta fuori dal sito, su una pagina che somiglia a
 * StudyHub e chiede di nuovo la password.
 *
 * **Il controllo non elenca le forme vietate: verifica quella permessa.**
 * La prima versione rifiutava `//esempio.it` e si riteneva a posto. Non lo era:
 * `/\esempio.it` passava indisturbato, perché nell'analisi di un indirizzo
 * http la barra rovesciata vale quanto quella dritta, e
 * `new URL('/\esempio.it', 'https://studio.dondolini.it')` dà
 * `https://esempio.it/`. Misurato il 6 settembre 2026.
 *
 * Elencare i caratteri pericolosi è una gara che si perde sempre: dopo la
 * barra rovesciata verrebbero la tabulazione e l'a-capo, che gli analizzatori
 * di indirizzi tolgono prima di leggere. Quindi si fa il contrario — si
 * risolve il valore contro un'origine finta e si **verifica che l'origine non
 * sia cambiata**. Se il valore riesce a portare altrove, qualunque sia il
 * trucco, l'origine cambia e si torna alla pagina iniziale.
 */
const ORIGINE_DI_PROVA = "https://studyhub.invalid";

/**
 * Il secondo passo dell'accesso: le sei cifre.
 *
 * Sta **sotto `/accesso`** e non altrove, e non è una scelta di forma: il
 * middleware considera pubblico tutto quello che comincia per `/accesso/`, e
 * chi arriva qui non ha ancora una sessione — l'ha appena avuta e cancellata la
 * libreria, in attesa del codice. Metterlo fuori vorrebbe dire aggiungere
 * un'eccezione al middleware, cioè una riga in più da ricordarsi.
 */
export const PERCORSO_SECONDO_PASSO = "/accesso/codice";

/**
 * **L'intestazione con cui una pagina scopre il proprio indirizzo.**
 *
 * *Milestone 9d, 23 settembre 2026.* Un componente server di Next.js non sa su
 * quale indirizzo sta girando, e `richiediSessione` — quella che rimanda ad
 * «Accedi» quando la sessione non c'è — ne ha bisogno per scrivere il `?torna=`.
 * La scrive `src/proxy.ts` su ogni richiesta, e la **sovrascrive**: un
 * valore mandato dal browser non sopravvive a quella riga.
 *
 * **Sta qui e non nel middleware** perché è un pezzo del meccanismo del ritorno,
 * insieme alle due funzioni qui sotto; e perché un file che gira dentro le pagine
 * non deve importare il middleware per una costante, tirandosi dietro
 * `next/server` dove non serve.
 *
 * Il valore che arriva di lì passa comunque da `percorsoDiRitorno`: la convalida
 * è una sola, ed è quella.
 */
export const INTESTAZIONE_PERCORSO = "x-studyhub-percorso";

export function percorsoDiRitorno(valore: unknown): string {
  if (typeof valore !== "string" || !valore.startsWith("/")) {
    return "/";
  }

  let risolto: URL;
  try {
    risolto = new URL(valore, ORIGINE_DI_PROVA);
  } catch {
    return "/";
  }

  if (risolto.origin !== ORIGINE_DI_PROVA) {
    return "/";
  }

  // Si restituisce la forma **normalizzata** e non quella ricevuta: è quella
  // di cui abbiamo appena dimostrato dove porta.
  return risolto.pathname + risolto.search + risolto.hash;
}

/**
 * Un indirizzo di StudyHub con appeso il `?torna=`, quando serve.
 *
 * Il secondo fattore aggiunge un passaggio in mezzo all'accesso, e quel
 * passaggio deve **portarsi dietro dove si stava andando**: senza, chi apre un
 * collegamento a «Utenti» da un telefono non collegato scriverebbe password e
 * sei cifre per ritrovarsi sulla pagina iniziale. Il valore che entra qui è già
 * passato da `percorsoDiRitorno`, quindi è un percorso interno; `URLSearchParams`
 * lo codifica comunque.
 *
 * Quando si torna alla pagina iniziale il parametro non si scrive: un indirizzo
 * con scritto `?torna=/` non aggiunge niente e si fa solo notare.
 */
export function conRitorno(percorso: string, torna: string): string {
  if (torna === "/") {
    return percorso;
  }

  return `${percorso}?${new URLSearchParams({ torna }).toString()}`;
}
